import React, { useState, useEffect, useMemo, useRef } from 'react';
import { classificationApi, TaxCategory, TaxabilityInfo } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const _catCache: Record<string, Promise<TaxCategory[]>> = {};
const _topStatesCache: Record<string, string[]> = {};

interface TicPickerProps {
  onSelect: (category: TaxCategory) => void;
  onClose: () => void;
}

function TaxabilityBadge({ status, rateModifier }: { status: string; rateModifier?: number | null }): JSX.Element {
  type BadgeCfg = { label: string; bg: string; color: string; border: string };
  const MAP: Record<string, BadgeCfg> = {
    taxable: { label: 'Taxable',  bg: '#F0FDF4', color: '#16A34A', border: '#BBF7D0' },
    exempt:  { label: 'Exempt',   bg: '#EFF6FF', color: '#2563EB', border: '#BFDBFE' },
    reduced: { label: `Reduced${rateModifier != null ? ` (${Math.round(rateModifier * 100)}%)` : ''}`, bg: '#FFFBEB', color: '#CA8A04', border: '#FEF08A' },
  };
  const cfg: BadgeCfg = MAP[status] ?? { label: status, bg: '#F9FAFB', color: '#6B7280', border: '#E5E7EB' };
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, whiteSpace: 'nowrap' }}>
      {cfg.label}
    </span>
  );
}

export function TicPicker({ onSelect, onClose }: TicPickerProps): JSX.Element {
  const { organizationId } = useAuth();

  const [categories, setCategories] = useState<TaxCategory[]>([]);
  const [topStates,  setTopStates]  = useState<string[]>([]);
  const [catLoading, setCatLoading] = useState(true);
  const [query,      setQuery]      = useState('');
  const [hovered,    setHovered]    = useState<string | null>(null);
  const [taxability, setTaxability] = useState<Record<string, TaxabilityInfo>>({});
  const [taxLoading, setTaxLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!_catCache[organizationId]) {
      _catCache[organizationId] = classificationApi.getTaxCategories(organizationId)
        .catch((): TaxCategory[] => { delete _catCache[organizationId]; return []; });
    }
    _catCache[organizationId]?.then(cats => { setCategories(cats); setCatLoading(false); });
  }, [organizationId]);

  useEffect(() => {
    if (_topStatesCache[organizationId]) { setTopStates(_topStatesCache[organizationId] ?? []); return; }
    classificationApi.getTopStates(organizationId)
      .then(states => { _topStatesCache[organizationId] = states; setTopStates(states); })
      .catch(() => setTopStates([]));
  }, [organizationId]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(c =>
      c.tic_code.includes(q) ||
      c.tic_name.toLowerCase().includes(q) ||
      (c.description ?? '').toLowerCase().includes(q)
    );
  }, [categories, query]);

  useEffect(() => {
    if (!hovered || topStates.length === 0) { setTaxability({}); return; }
    setTaxLoading(true);
    const timer = setTimeout(() => {
      classificationApi.getTaxability(hovered, topStates)
        .then(data => setTaxability(data))
        .catch(() => setTaxability({}))
        .finally(() => setTaxLoading(false));
    }, 150);
    return () => clearTimeout(timer);
  }, [hovered, topStates]);

  const hoveredCat = categories.find(c => c.tic_code === hovered) ?? null;

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'Enter' && hoveredCat) { onSelect(hoveredCat); return; }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const idx  = filtered.findIndex(c => c.tic_code === hovered);
      const next = e.key === 'ArrowDown'
        ? Math.min(idx + 1, filtered.length - 1)
        : Math.max(idx - 1, 0);
      const nextItem = filtered[next];
      if (nextItem) setHovered(nextItem.tic_code);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()} onKeyDown={handleKeyDown}>
        <div style={S.header}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15, color: '#0F172A' }}>Select TIC Code</div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>
              SST Taxability Information Code — determines tax treatment per state
            </div>
          </div>
          <button onClick={onClose} style={S.closeBtn} aria-label="Close">✕</button>
        </div>

        <div style={S.searchRow}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0 }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by name, code, or description…"
            style={S.searchInput}
          />
          {query && (
            <button onClick={() => setQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
          )}
        </div>

        <div style={S.body}>
          <div style={S.list}>
            {catLoading ? (
              <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, padding: '8px 4px', alignItems: 'center' }}>
                    <div className="skeleton-box" style={{ width: 40, height: 12, borderRadius: 3 }} />
                    <div style={{ flex: 1 }}>
                      <div className="skeleton-box" style={{ width: '70%', height: 12, marginBottom: 4 }} />
                      <div className="skeleton-box" style={{ width: '50%', height: 10 }} />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div style={S.emptyList}>
                <div style={{ fontSize: 24, marginBottom: 6 }}>🔍</div>
                <div style={{ fontWeight: 600, color: '#334155', fontSize: 13 }}>No results for "{query}"</div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 3 }}>Try searching by TIC code number</div>
              </div>
            ) : (
              filtered.map(cat => {
                const isActive = hovered === cat.tic_code;
                return (
                  <div
                    key={cat.tic_code}
                    onMouseEnter={() => setHovered(cat.tic_code)}
                    onClick={() => onSelect(cat)}
                    style={{ ...S.listItem, background: isActive ? '#EFF6FF' : 'transparent', borderLeft: `3px solid ${isActive ? '#1B4F8C' : 'transparent'}` }}
                  >
                    <span style={S.ticCode}>{cat.tic_code}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: isActive ? 600 : 400, color: '#0F172A' }}>{cat.tic_name}</div>
                      {cat.description && (
                        <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {cat.description}
                        </div>
                      )}
                    </div>
                    {isActive && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B4F8C" strokeWidth="2" style={{ flexShrink: 0 }}>
                        <polyline points="9 18 15 12 9 6"/>
                      </svg>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div style={S.preview}>
            {!hoveredCat ? (
              <div style={S.previewEmpty}>
                <div style={{ fontSize: 28, marginBottom: 8 }}>←</div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>Hover a TIC code</div>
                <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4, textAlign: 'center', lineHeight: 1.5 }}>
                  See taxability per state before you assign it
                </div>
              </div>
            ) : (
              <>
                <div style={S.previewHeader}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: '#1B4F8C', letterSpacing: '-.01em' }}>{hoveredCat.tic_code}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', marginTop: 2 }}>{hoveredCat.tic_name}</div>
                  {hoveredCat.description && (
                    <div style={{ fontSize: 11, color: '#64748B', marginTop: 4, lineHeight: 1.5 }}>{hoveredCat.description}</div>
                  )}
                </div>

                <div style={S.previewSection}>
                  <div style={S.previewLabel}>
                    Taxability preview
                    <span style={{ fontWeight: 400, marginLeft: 6 }}>· your top states</span>
                  </div>

                  {topStates.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#94A3B8', fontStyle: 'italic' }}>Top states not loaded yet</div>
                  ) : taxLoading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {topStates.map(s => (
                        <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #F1F5F9' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{s}</span>
                          <div className="skeleton-box" style={{ width: 70, height: 20, borderRadius: 10 }} />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      {topStates.map(s => {
                        const t = taxability[s];
                        return (
                          <div key={s} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 0', borderBottom: '1px solid #F8FAFC' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>{s}</span>
                            {t
                              ? <TaxabilityBadge status={t.status} rateModifier={t.rate_modifier} />
                              : <span style={{ fontSize: 10, color: '#CBD5E1', fontStyle: 'italic' }}>No data yet</span>
                            }
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {!taxLoading && topStates.length > 0 && Object.keys(taxability).length === 0 && (
                    <div style={{ marginTop: 12, padding: '8px 10px', background: '#FFFBEB', border: '1px solid #FEF08A', borderRadius: 6 }}>
                      <div style={{ fontSize: 10, color: '#713F12', lineHeight: 1.5 }}>
                        SALT Advisor taxability matrix not yet imported for this TIC code. Tax accuracy requires this data before filing.
                      </div>
                    </div>
                  )}
                </div>

                <button onClick={() => onSelect(hoveredCat)} style={S.selectBtn}>
                  Use {hoveredCat.tic_code} — {hoveredCat.tic_name}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={S.footer}>
          <span>↑↓ navigate</span>
          <span>Enter to select</span>
          <span>Esc to close</span>
          <span style={{ marginLeft: 'auto', color: '#CBD5E1' }}>
            {catLoading ? '…' : `${filtered.length} of ${categories.length} codes`}
          </span>
        </div>
      </div>
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  overlay:      { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 },
  modal:        { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 720, maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(15,23,42,0.28), 0 4px 16px rgba(15,23,42,0.12)', overflow: 'hidden' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px 0' },
  closeBtn:     { background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#94A3B8', padding: '0 2px', lineHeight: 1, flexShrink: 0 },
  searchRow:    { display: 'flex', alignItems: 'center', gap: 8, margin: '12px 20px 0', padding: '8px 12px', border: '1.5px solid #E2E8F0', borderRadius: 8, background: '#F8FAFC' },
  searchInput:  { flex: 1, border: 'none', background: 'none', outline: 'none', fontSize: 13, color: '#0F172A', fontFamily: 'inherit' },
  body:         { display: 'flex', flex: 1, minHeight: 0, margin: '12px 0 0', borderTop: '1px solid #F1F5F9', overflow: 'hidden' },
  list:         { flex: 1, overflowY: 'auto', borderRight: '1px solid #F1F5F9' },
  listItem:     { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 16px 9px 12px', cursor: 'pointer', transition: 'background .08s', borderBottom: '1px solid #F8FAFC' },
  ticCode:      { fontWeight: 700, fontSize: 11, color: '#1B4F8C', minWidth: 44, fontFamily: 'monospace', flexShrink: 0 },
  emptyList:    { padding: '40px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  preview:      { width: 220, flexShrink: 0, padding: '14px 16px', background: '#FAFAFA', display: 'flex', flexDirection: 'column', overflowY: 'auto' },
  previewEmpty: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: '#CBD5E1' },
  previewHeader:{ paddingBottom: 12, borderBottom: '1px solid #E2E8F0', marginBottom: 12 },
  previewSection:{ flex: 1 },
  previewLabel: { fontSize: 10, fontWeight: 800, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 },
  selectBtn:    { marginTop: 14, width: '100%', padding: '9px 12px', background: '#1B4F8C', color: '#fff', border: 'none', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left', lineHeight: 1.4 },
  footer:       { display: 'flex', gap: 16, alignItems: 'center', padding: '8px 20px', borderTop: '1px solid #F1F5F9', fontSize: 10, color: '#94A3B8', background: '#FAFAFA' },
};
